import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { PlanService } from '../../shared/services/plan.service';
import { PreferenceService } from '../../shared/services/preference.service';
import { TeamService } from '../../shared/services/team.service';
import { UserService } from '../../shared/services/user.service';
import { forkJoin } from 'rxjs';
import { Utilities } from '../../shared/utilities';
import { User } from '../../shared/classes/user';
import { Subscription } from '../../shared/classes/subscription';
import { Preference } from '../../shared/classes/preference';
import { Team } from '../../shared/classes/team';

@Component({
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css']
})

export class UserProfileComponent implements OnInit, OnDestroy {

  public activeTab = 1;
  public loading = true;
  public processing = false;
  public user: User;
  public newPassword = '';
  public passwordConfirm = '';
  public error = '';
  public message = '';
  public editingGeneralData = true;
  public editingPassword = false;
  public userSubscriptions: Subscription [] = [];
  public teams: Team [] = [];
  public selectedFavouriteTeam: Team;

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private planService: PlanService,
    private preferenceService: PreferenceService,
    private teamService: TeamService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
  }

  loadData(successMessage?: string): void {
    const that = this;
    that.loading = true;
    that.userSubscriptions = [];
    that.teams = [];
    that.user = User.parse(that.authenticationService.loggedInUser);
    if (!that.user) {
      that.error = '<p><b>No se han podido cargar sus datos.</b></p><p>Por favor, intente nuevamente.</p>';
      that.loading = false;
    } else {
      forkJoin([
        that.userService.getUserSubscriptions(!!that.user),
        that.preferenceService.getPreferences(),
        that.teamService.getTeams()
      ]).subscribe(([getUserSubscriptionsResponse, getPreferencesResponse, getTeamsResponse]) => {
          // tslint:disable-next-line:max-line-length
          if (getUserSubscriptionsResponse && getUserSubscriptionsResponse.Suscripciones && getUserSubscriptionsResponse.Suscripciones.length > 0) {
            getUserSubscriptionsResponse.Suscripciones.forEach(
              oneUserSubscription => {
                const subscription = Subscription.parse(oneUserSubscription);
                that.userSubscriptions.push(subscription);
              }
            );
          }
          if (getPreferencesResponse && getPreferencesResponse.Preferencias && getPreferencesResponse.Preferencias.length > 0) {
            getPreferencesResponse.Preferencias.forEach(
              onePreference => {
                const preference = Preference.parse(onePreference);
                const userHasPreference = !!that.user.UserPreferences.find(p => p.PreferenceId === preference.PreferenceId);
                if (!userHasPreference) {
                  that.user.UserPreferences.push(preference);
                }
              }
            );
          }
          that.teams.push(
            Team.parse({
              TeamId: '',
              TeamName: 'Ninguno'
            })
          );
          if (getTeamsResponse && getTeamsResponse.Equipos && getTeamsResponse.Equipos.length > 0) {
            getTeamsResponse.Equipos.forEach(
              oneTeam => {
                const team = Team.parse(oneTeam);
                that.teams.push(team);
              }
            );
          }
          // tslint:disable-next-line:max-line-length
          that.selectedFavouriteTeam = that.teams.find(t => that.user.UserFavouriteTeam && that.user.UserFavouriteTeam.TeamId === t.TeamId);
          if (successMessage) {
            that.message = successMessage;
          }
          that.loading = false;
      },
      (error) => {
        Utilities.logError('Error al cargar los datos para user-profile.component');
        Utilities.logError(error);
        that.loading = false;
        that.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
      });
    }
  }

  public refreshPage(successMessage?: string): void {
    this.loadData(successMessage);
  }

  public updatePassword(): void {
    const that = this;
    if (!that.processing) {
      that.processing = true;
      if (that.validatePasswords()) {
        forkJoin([
          that.userService.changePassword(that.user.UserPassword, that.newPassword)
        ]).subscribe(([changePasswordResponse]) => {
            if (changePasswordResponse) {
              if (!changePasswordResponse.HasError) {
                that.resetFormData();
                that.message = '<p><b>La contraseña ha sido modificada correctamente.</b></p>';
              } else {
                if (changePasswordResponse.ErrorMessage){
                  that.error = `<p><b>¡Error!</b></p><p>${changePasswordResponse.ErrorMessage}</p>`;
                } else {
                  that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
                }
              }
            } else {
              that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
            }
            that.processing = false;
          },
          (error) => {
            Utilities.logError('Error al actualizar los datos generales');
            Utilities.logError(error);
            that.processing = false;
            localStorage.clear();
            that.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
          });
      } else {
        that.processing = false;
      }
    }
  }

  public unsubscribeToPlan(planId: string): void {
    const that = this;
    if (!that.processing) {
      that.error = '';
      that.message = '';
      that.processing = true;
      forkJoin([
        that.userService.unsubscribeToPlan(planId)
      ]).subscribe(([unsubscribeToPlanResponse]) => {
          if (unsubscribeToPlanResponse) {
            if (!unsubscribeToPlanResponse.HasError) {
              that.refreshPage('<p><b>Suscripción cancelada con éxito.</b></p>');
            } else {
              if (unsubscribeToPlanResponse.ErrorMessage){
                that.error = `<p><b>¡Error!</b></p><p>${unsubscribeToPlanResponse.ErrorMessage}</p>`;
              } else {
                that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
              }
            }
          } else {
            that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
          }
          that.processing = false;
        },
        (error) => {
          Utilities.logError('Error al desuscribirse de un plan');
          Utilities.logError(error);
          that.processing = false;
          localStorage.clear();
          that.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
        });
    }
  }

  public goTo(url): void {
    this.router.navigate([url]);
  }

  public setActiveTab(tab: number): void {
    this.resetFormData();
    this.activeTab = tab;
  }

  public switchGeneralDataAndPassword(): void {
    this.resetFormData();
    this.editingGeneralData = !this.editingGeneralData;
    this.editingPassword = !this.editingPassword;
  }

  public updateGeneralData(): void {
    const that = this;
    if (!that.processing) {
      that.processing = true;
      if (that.validateGeneralData()) {
        that.user.UserFavouriteTeam = that.selectedFavouriteTeam;
        forkJoin([
          that.userService.updateGeneralData(that.user)
        ]).subscribe(([updateGeneralDataResponse]) => {
            if (updateGeneralDataResponse) {
              if (!updateGeneralDataResponse.HasError) {
                that.authenticationService.loggedInUser = Object.assign({}, that.user);
                that.refreshPage('<p><b>El usuario ha sido modificado correctamente.</b></p>');
              } else {
                if (updateGeneralDataResponse.ErrorMessage){
                  that.error = `<p><b>¡Error!</b></p><p>${updateGeneralDataResponse.ErrorMessage}</p>`;
                } else {
                  that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
                }
              }
            } else {
              that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
            }
            that.processing = false;
          },
          (error) => {
            Utilities.logError('Error al actualizar los datos generales');
            Utilities.logError(error);
            that.processing = false;
            localStorage.clear();
            that.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
          });
      } else {
        that.processing = false;
      }
    }
  }

  public validateGeneralData(): boolean {
    const that = this;
    that.error = '';
    that.message = '';
    let dataIsValid = true;
    if (
      that.user.UserFirstName &&
      that.user.UserFirstName.trim().length > 0 &&
      that.user.UserLastName &&
      that.user.UserLastName.trim().length > 0 &&
      that.user.UserEmail &&
      that.user.UserEmail.trim().length > 0 &&
      that.user.UserBirthDate &&
      that.user.UserBirthDate.trim().length > 0
      // De momento, en número de teléfono no es obligatorio
      /*that.user.UserPhoneNumber &&
      that.user.UserPhoneNumber.trim().length > 0*/
    ) {
      that.user.UserFirstName = that.user.UserFirstName.trim();
      that.user.UserLastName = that.user.UserLastName.trim();
      that.user.UserEmail = that.user.UserEmail.trim();
      that.user.UserBirthDate = that.user.UserBirthDate.trim();
      that.user.UserPhoneNumber = that.user.UserPhoneNumber.trim();
      if (that.user.UserFirstName.length < 2) {
        dataIsValid = false;
        that.error += '<p>El campo de nombres debe contener al menos 2 caracteres.</p>';
      }
      if (that.user.UserLastName.length < 2) {
        dataIsValid = false;
        that.error += '<p>El campo de apellidos debe contener al menos 2 caracteres.</p>';
      }
      if (!that.user.UserEmail.match(Utilities.EXP_REG_FORMATO_EMAIL)) {
        dataIsValid = false;
        that.error += '<p>El email no tiene un formato válido.</p>';
      }
      const dateValidation = Utilities.dateValidation(that.user.UserBirthDate, 'fecha de nacimiento', '1900-01-01');
      if (!dateValidation.dateIsValid) {
        dataIsValid = false;
        dateValidation.errors.forEach(error => that.error += `<p>${error}</p>`);
      }
    } else {
      dataIsValid = false;
      that.error += '<p><b>Complete el formulario.</b></p><p>Todos los campos son obligatorios.</p>';
    }
    return dataIsValid;
  }

  public validatePasswords(): boolean {
    const that = this;
    that.error = '';
    that.message = '';
    let dataIsValid = true;
    if (
      that.user.UserPassword &&
      that.user.UserPassword.trim().length > 0 &&
      that.newPassword &&
      that.newPassword.trim().length > 0 &&
      that.passwordConfirm &&
      that.passwordConfirm.trim().length > 0
    ) {
      that.user.UserPassword = that.user.UserPassword.trim();
      that.newPassword = that.newPassword.trim();
      that.passwordConfirm = that.passwordConfirm.trim();
      if (that.newPassword !== that.passwordConfirm) {
        dataIsValid = false;
        that.error += '<p>La contraseña y la verificación deben ser iguales.</p>';
      } else if (that.user.UserPassword === that.newPassword) {
        dataIsValid = false;
        that.error += '<p>La contraseña anterior y la nueva no pueden ser iguales.</p>';
      }
      if (!dataIsValid) {
        that.error = '<p><b>Por favor, solucione los errores y vuelva a intentar.</b></p>' + that.error;
      }
    } else {
      dataIsValid = false;
      that.error += '<p><b>Complete el formulario.</b></p><p>Todos los campos son obligatorios.</p>';
    }
    return dataIsValid;
  }

  resetFormData(): void {
    this.message = '';
    this.error = '';
    this.user = User.parse(this.authenticationService.loggedInUser);
    this.newPassword = '';
    this.passwordConfirm = '';
  }
}
